import { z } from "zod";
import { idSchema, instantSchema } from "@amber/contracts";
import { languageCodeSchema, type PROFILE_LIFECYCLE_STATES } from "./records.js";
import { nameSchema, roleSchema } from "./identity.js";

export const MACHINE_TRACKING_STATES = ["tracked", "paused", "draft", "not_tracked", "retired"] as const;
export const machineTrackingStateSchema = z.enum(MACHINE_TRACKING_STATES);
export type MachineTrackingState = z.infer<typeof machineTrackingStateSchema>;

export const TRACKING_STATE_OF: Record<(typeof PROFILE_LIFECYCLE_STATES)[number], MachineTrackingState> = {
  active: "tracked",
  paused: "paused",
  draft: "draft",
  incidental: "not_tracked",
  archived: "retired",
};

/** PS-21: the active roster, listed when no state filter is given. */
export const ACTIVE_ROSTER_STATES: readonly MachineTrackingState[] = ["tracked", "paused"];

export const MAX_PROFILE_PAGE = 200;

export const machineProfilesQuerySchema = z.strictObject({
  state: z
    .string()
    .transform((s) => [...new Set(s.split(",").filter((v) => v.length > 0))])
    .pipe(z.array(machineTrackingStateSchema).min(1))
    .optional(),
  page_size: z.coerce.number().int().min(1).max(MAX_PROFILE_PAGE).default(50),
  cursor: z.string().min(1).max(512).optional(),
});
export type MachineProfilesQuery = z.infer<typeof machineProfilesQuerySchema>;

export const machinePlanSummarySchema = z.strictObject({
  applied: z.boolean(),
  pending: z.boolean(),
  applied_at: instantSchema.nullable(),
  monitoring_start: instantSchema.nullable(),
  review_date: z.iso.date().nullable(),
  sources: z.number().int().nonnegative(),
});

export const machineProfileSchema = z.strictObject({
  profile_id: idSchema("prf"),
  display_name: z.string(),
  tracking_state: machineTrackingStateSchema,
  names: z.array(nameSchema),
  roles: z.array(roleSchema),
  languages: z.array(languageCodeSchema),
  birth_date: z.string().nullable(),
  wikidata_id: z.string().nullable(),
  wikipedia: z.record(z.string(), z.string()),
  plan: machinePlanSummarySchema.nullable(),
  revision: z.number().int().positive(),
  created_at: instantSchema,
});
export type MachineProfile = z.infer<typeof machineProfileSchema>;

export const machineProfilePageSchema = z.strictObject({
  as_of: instantSchema,
  read_at: instantSchema,
  states: z.array(machineTrackingStateSchema),
  profiles: z.array(machineProfileSchema),
  next_cursor: z.string().nullable(),
});
export type MachineProfilePage = z.infer<typeof machineProfilePageSchema>;

export const machineProfileReadSchema = z.strictObject({
  as_of: instantSchema,
  resolved_from: z.strictObject({ profile_id: idSchema("prf"), merge_id: idSchema("mrg") }).nullable(),
  profile: machineProfileSchema,
});
export type MachineProfileRead = z.infer<typeof machineProfileReadSchema>;
